<template>
    <b-modal ref="autoresponderRuleEditor" @hide="$emit('hide')" @ok="doSave"
            size="xl" v-model="show" no-close-on-backdrop scrollable button-size="sm" :ok-disabled="$v.$invalid">
        <template v-slot:modal-title>{{$t('title', { name: rule.name})}}</template>
        <template v-slot:modal-footer="{ ok, cancel, hide }">
            <autoresponder-preview :disabled="rule.autoresponderid === 0" size="sm" :autoresponder="autoresponder"
                    :rule="rule"></autoresponder-preview>
            <b-button size="sm" @click="cancel">{{$t('buttonCancel')}}</b-button>
            <b-button size="sm" @click="ok"  variant="primary" :disabled="$v.invalid">{{$t('buttonOK')}}</b-button>
        </template>
        <b-container fluid class="autoresponderRuleEditor">
            <b-row>
                <b-col cols="6">
                    <b-form-group :label="$t('name')" size="sm">
                        <b-input v-model="rule.name" :state="!$v.rule.name.$invalid" size="sm"
                                :placeholder="$t('namePlaceholder')"></b-input>
                        <input-feedback :validation="$v.rule.name"></input-feedback>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('autoresponder')" size="sm">
                        <b-select v-model="rule.autoresponderid" size="sm" :options="autoresponderOptions"></b-select>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group>
                        <b-checkbox v-model="rule.ruleenable">{{$t('ruleEnable')}}</b-checkbox>
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <h2>{{$t('triggerConditions')}}</h2>
                    <small>{{$t('triggerConditionInfo')}}</small>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('dates')">
                        <b-container fluid>
                            <b-row>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.datefrom" date-only optional></date-time-picker>
                                </b-col>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.dateto" date-only optional></date-time-picker>
                                </b-col>
                            </b-row>
                        </b-container>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('times')">
                        <b-container fluid>
                            <b-row>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.timefrom" time-only optional></date-time-picker>
                                </b-col>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.timeto" time-only optional></date-time-picker>
                                </b-col>
                            </b-row>
                        </b-container>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('dateTimes')">
                        <b-container fluid>
                            <b-row>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.datetimefrom" optional></date-time-picker>
                                </b-col>
                                <b-col cols="6">
                                    <date-time-picker v-model="rule.datetimeto" optional></date-time-picker>
                                </b-col>
                            </b-row>
                        </b-container>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('daysOfWeek')">
                        <b-checkbox-group :options="dowOptions" v-model="ruleDaysOfWeek"></b-checkbox-group>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('subject')" :description="$t('subjectDescription')">
                        <b-input v-model="rule.subject" :placeholder="$t('subjectPlaceholder')"></b-input>
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group :label="$t('sender')" :description="$t('senderDescription')">
                        <b-input v-model="rule.sender" :placeholder="$t('senderPlaceholder')"></b-input>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-container>
        <autoresponder-template-variables v-model="rule.customfields"
                :template="autoresponderText"></autoresponder-template-variables>
        <!--{{rule}} {{ruleDaysOfWeek}} {{autoresponderText}}-->
    </b-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import InputFeedback from '@/components/inputFeedback';
import DateTimePicker from './dateTimePicker';
import { DateTime } from 'luxon';
import AutoresponderTemplateVariables from '@/components/autoresponderTemplateVariables';
import AutoresponderPreview from '@/components/autoresponderPreview';

export default {
  name: 'autoresponderRuleEditor',
  components: { AutoresponderTemplateVariables, DateTimePicker, InputFeedback, AutoresponderPreview },
  i18nOptions: { keyPrefix: 'settings.autoresponders.advanced.autoresponderRuleEditor' },
  data: function() {
    return {
      rule: { autoresponderenable: false, autoresponderid: 0, id: 0, name: '', daysofweek: '' },
      show: false
    };
  },
  props: {
    value: Object,
    autoresponders: Array
  },
  validations: {
    rule: {
      name: { required }
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.loadData(newVal);
        if (newVal.id) {
          this.show = true;
        }
      } else {
        this.rule.id = 0;
        this.show = false;
      }
    }
  },
  methods: {
    loadData(value) {
      this.rule = Object.assign({}, value);
      if (!this.rule.autoresponderenable) {
        this.rule.autoresponderid = 0;
      }
    },
    doSave($evt) {
      if (this.rule.autoresponderid === 0) {
        this.rule.autoresponderid = this.autoresponders[0].id;
        this.rule.autoresponderenable = false;
      } else {
        this.rule.autoresponderenable = true;
      }
      this.$emit('input', this.rule);
    }
  },
  computed: {
    autoresponderOptions() {
      return [{ text: this.$t('autoresponderNone'), value: 0 },
        ...this.autoresponders.map(a => {
          if (a.owner === 0) {
            return { text: this.$t('autoresponderNameGlobal', { name: a.name }), value: a.id };
          } else {
            return { text: this.$t('autoresponderNamePersonal', { name: a.name }),
              value: a.id };
          }
        })];
    },
    dowOptions() {
      let days = [];
      let start = DateTime.fromISO('2020-05-24');
      for (let i = 0; i < 7; i++) {
        days.push({ value: i, text: start.plus({ day: i }).toLocaleString({ weekday: 'short' }) });
      }

      return days;
    },
    ruleDaysOfWeek: {
      get() {
        return this.rule.daysofweek.split('').map(d => parseInt(d, 10));
      },
      set(v) {
        this.rule.daysofweek = v.join('');
      }
    },
    autoresponder() {
      return this.autoresponders.find(a => a.id === this.rule.autoresponderid);
    },
    autoresponderText() {
      if (this.rule.autoresponderenable) {
        if (this.autoresponder) {
          return this.autoresponder.text;
        }
      }
      return '';
    }
  }
};
</script>

<style scoped lang="scss">

</style>
